import { VehicleOwner, VehicleStatus } from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { VehicleRelatedDto } from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import { CrossIcon, Plus3Icon } from "@/shared/icons";
import { Button, Icon } from "@/shared/ui";
import VehicleCardButtonDepricated from "@/shared/widgets/VehicleCardButtonDepricated";
import EstateVehicleOwner from "@/domains/estate/components/vehicles/EstateVehicleOwner";

type Props = {
  className?: string;
  vehicles: VehicleRelatedDto[];
  onSelectVehicleId: (vehicleId: number) => void;
  onResetParking: (vehicleId: number) => void;
  onAddVehicle: (index: number) => void;
};

export default function EstateVehiclesList({
  className,
  vehicles,
  onSelectVehicleId,
  onResetParking,
  onAddVehicle,
}: Props) {
  const { t } = useTranslation("estate");

  return (
    <div className={cn("grid grid-cols-4 gap-1.5", className)}>
      {vehicles.map((vehicle, index) => {
        if (vehicle) {
          const isHaveAccess = vehicle.relation.isOwner || vehicle.relation.isShared;

          return (
            <VehicleCardButtonDepricated
              key={vehicle.vehicleId}
              full
              model={vehicle.model}
              name={vehicle.name}
              plateNumber={vehicle.plateNumber}
              status={vehicle.status}
              // fuelType={VehicleFuelType.Diesel}
              className="w-full h-[17.5rem]"
              onClick={() => onSelectVehicleId(vehicle.vehicleId)}
            >
              <Button
                variant="solid-surface"
                color="gray"
                className="absolute right-2.5 top-2.5 p-0 w-[1.5rem] h-[1.5rem] border-0 group-hover:visible invisible"
                onClick={(e) => {
                  onResetParking(vehicle.vehicleId);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <CrossIcon size={8} className="text-accent-solid" />
              </Button>

              {isHaveAccess && (
                <div
                  className={clsx(
                    "flex items-center justify-center z-1",
                    "absolute left-0 top-0 w-[1.5rem] h-[1.5rem] rounded-[0.5rem] bg-gray/50",
                  )}
                >
                  <Icon name="key" category="speedometer" className="w-4 bg-inverted" />
                </div>
              )}

              {vehicle.owner && vehicle.owner.type === VehicleOwner.Character && (
                <EstateVehicleOwner
                  owner={vehicle.owner.content.name}
                  className="z-2 absolute right-5 top-[12.2rem]"
                />
              )}

              {vehicle.status !== VehicleStatus.Pending && (
                <div
                  className={clsx(
                    "absolute z-2 left-5 top-[12rem] text-10 font-bold text-gray",
                    "bg-gradient-to-r from-gray/50 via-50% via-gray/50 to-accent-muted/30 py-2 px-2.5 rounded-[0.375rem] backdrop-blur-[2px]",
                  )}
                >
                  {t(`estate.vehicles.status.${vehicle.status.toLowerCase()}`)}
                </div>
              )}
            </VehicleCardButtonDepricated>
          );
        }
        return (
          <button
            type="button"
            key={`${index + index}`}
            className={clsx(
              "flex flex-col items-center justify-center gap-3.5 p-2.5 bg-dark-800/20 rounded-[0.75rem] border border-dashed border-[#1A1A1A] h-[12.5rem] cursor-pointer",
              "text-10 font-bold uppercase text-white/50",
              "group",
            )}
            onClick={() => onAddVehicle(index)}
          >
            <Plus3Icon
              size={38}
              className="transition-all duration-200 ease-in-out group-hover:text-accent-muted"
            />
            <div>{t("estate.vehicles.addVehicle")}</div>
          </button>
        );
      })}
    </div>
  );
}
